// parts/TranscriptPortal.tsx — inline version (no createPortal)
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { UpdateEvent } from "../../../types/domain";
import type { ReplaySpeed, ReplayState } from "../hooks/useReplayState";
import { ReplayControls } from "./ReplayControls";
import "./TranscriptPortal.css";

type Props = {
  panelRef?: React.RefObject<HTMLDivElement | null>;
  open: boolean;
  onClose: () => void;
  latestSeq: number;
  bubbles: UpdateEvent[];
  pinnedSeqs: Set<number>;
  togglePin: (seq: number) => void;
  roleClass?: (speaker?: string) => "spn-bubble--client" | "spn-bubble--therapist";
  mode?: "history" | "live" | "replay";
  liveTranscript?: string;
  title?: string;
  recording?: boolean;
  replayState?: ReplayState;
  onPlay?: () => void;
  onPause?: () => void;
  onSeek?: (time: number) => void;
  onSpeedChange?: (speed: ReplaySpeed) => void;
  onExitReplay?: () => void;
  // kept for API compat but ignored in inline mode
  width?: number;
  onResizeStart?: (e: React.MouseEvent) => void;
  sessionPanelWidth?: number;
  /** Deep-link target: highlights + scrolls to the turns matching a matched moment. */
  highlightRange?: { startMs: number; endMs: number } | null;
};

export const TranscriptPortal: React.FC<Props> = ({
  open,
  latestSeq,
  bubbles,
  pinnedSeqs,
  togglePin,
  roleClass,
  mode = "history",
  liveTranscript,
  title,
  recording,
  replayState,
  onPlay,
  onPause,
  onSeek,
  onSpeedChange,
  onExitReplay,
  highlightRange,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scrolledRangeRef = useRef<string | null>(null);

  const isLiveMode = mode === "live";
  const isReplayMode = mode === "replay";
  const headerTitle = title ?? (isReplayMode ? t("session.transcript") : isLiveMode ? t("session.liveTranscript") : t("session.transcript"));

  // Scroll to the first hit row once per distinct highlightRange. Session
  // switches load bubbles asynchronously, so highlightRange can arrive before
  // the matching turn exists in the DOM — this effect re-runs once it does.
  useEffect(() => {
    if (!highlightRange) {
      scrolledRangeRef.current = null;
      return;
    }
    const rangeKey = `${highlightRange.startMs}-${highlightRange.endMs}`;
    if (scrolledRangeRef.current === rangeKey) return;
    const container = scrollRef.current;
    if (!container) return;
    const hitRow = container.querySelector(".spn-tl-row--hit");
    if (!hitRow) return;
    hitRow.scrollIntoView({ block: "center" });
    scrolledRangeRef.current = rangeKey;
  }, [highlightRange, bubbles]);

  if (!open) return null;

  const who = (s?: string) => (s || "").toLowerCase().trim();
  const computeBubbleKind = (s?: string) =>
    roleClass
      ? roleClass(s)
      : who(s) === "client"
      ? "spn-bubble--client"
      : "spn-bubble--therapist";

  const fallbackText = (liveTranscript ?? "").trim();

  return (
    <div className="spn-transcriptInline">
      {isReplayMode && replayState && onPlay && onPause && onSeek && onSpeedChange && onExitReplay && (
        <ReplayControls
          currentTime={replayState.currentTime}
          duration={replayState.duration}
          isPlaying={replayState.isPlaying}
          speed={replayState.speed}
          onPlay={onPlay}
          onPause={onPause}
          onSeek={onSeek}
          onSpeedChange={onSpeedChange}
          onExitReplay={onExitReplay}
        />
      )}

      <div className="spn-transcript" ref={scrollRef}>
        {bubbles.length === 0 && !fallbackText && (
          <div className="spn-empty">
            {t("session.transcriptEmptyState").split("\n").map((line, i) => (
              <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
            ))}
          </div>
        )}

        {bubbles.map((b) => {
          const pinned = pinnedSeqs.has(b.seq);
          const kind = computeBubbleKind(b.speaker);
          const isClient = kind === "spn-bubble--client" || who(b.speaker) === "client";

          const start = (b as any).start_sec;
          const ts =
            typeof start === "number"
              ? `${String(Math.floor(start / 60)).padStart(2, "0")}:${String(Math.floor(start % 60)).padStart(2, "0")}`
              : "";

          const bubbleStartMs = (b.start_sec ?? -1) * 1000;
          const bubbleEndMs = (b.end_sec ?? -1) * 1000;
          const isHit =
            !!highlightRange &&
            bubbleStartMs < highlightRange.endMs &&
            bubbleEndMs > highlightRange.startMs;

          return (
            <div
              key={b.seq}
              className={`spn-tl-row ${isClient ? "spn-tl-row--client" : "spn-tl-row--therapist"} ${pinned ? "spn-tl-row--pinned" : ""} ${isHit ? "spn-tl-row--hit" : ""}`}
              onClick={() => togglePin(b.seq)}
              title={pinned ? t("session.unpin") : t("session.pinQuote")}
              role="button"
            >
              <span className="spn-tl-ts">{ts}</span>
              <div className="spn-tl-body">
                <div className="spn-tl-who">{b.speaker || "?"}</div>
                <div className="spn-tl-text">{(b.text ?? "").trim()}</div>
              </div>
            </div>
          );
        })}

        {bubbles.length === 0 && fallbackText && (
          <div className="spn-empty" style={{ whiteSpace: "pre-wrap" }}>
            {fallbackText}
          </div>
        )}
      </div>
    </div>
  );
};
